import React, { useEffect, useRef } from "react";
import { Icon, D } from "./icons";

/**
 * Wireframe dialog (`.modal > .dialog`) with the wireframe's own behaviour:
 * Escape / click on the backdrop closes it, focus moves to the close button and
 * is trapped inside (Tab / Shift+Tab), the page behind is inert and not
 * scrollable, focus returns to the previously focused element on close.
 */
export function Modal({ title, wide = false, full = false, onClose, children }) {
  const root = useRef(null);
  useEffect(() => {
    const last = document.activeElement;
    const behind = [...document.querySelectorAll("main, .sidebar, .topbar, .mobile-nav")];
    behind.forEach((el) => (el.inert = true));
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    root.current?.querySelector('[data-close="x"]')?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
      if (e.key === "Tab" && root.current) {
        const els = [...root.current.querySelectorAll("button,input,select,a[href],textarea")].filter(
          (el) => !el.disabled && el.offsetParent !== null,
        );
        if (!els.length) return;
        if (e.shiftKey && document.activeElement === els[0]) {
          e.preventDefault();
          els[els.length - 1].focus();
        } else if (!e.shiftKey && document.activeElement === els[els.length - 1]) {
          e.preventDefault();
          els[0].focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      behind.forEach((el) => (el.inert = false));
      document.body.style.overflow = prevOverflow;
      if (last && last.isConnected) last.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className={`modal${full ? " full" : ""}`} ref={root} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section aria-labelledby="dialog-title" aria-modal="true" className={`dialog${wide ? " map-dialog" : ""}`} role="dialog">
        <header>
          <h2 id="dialog-title">{title}</h2>
          <button aria-label="Закрыть" className="icon-button" data-close="x" onClick={onClose} type="button">
            <Icon d={D.close} />
          </button>
        </header>
        <div id="dialog-content">{children}</div>
      </section>
    </div>
  );
}

/** Wireframe `field()` helper: <label>text<input required></label>. */
export function Field({ label, name, value, onChange, type = "text", required = true, inputRef, ...rest }) {
  return (
    <label>
      {label}
      <input
        ref={inputRef}
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        required={required}
        {...(type === "number" ? { min: "0", step: "any" } : {})}
        {...rest}
      />
    </label>
  );
}
